import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import comms from "../comms";

function RouteSelection({ selectedVehicle, fleetSettings, onRouteSelected, onBack }) {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  useEffect(() => {
    fetchAvailableRoutes();
  }, [selectedVehicle]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAvailableRoutes = async () => {
    try {
      setLoading(true);
      const vehicleId = selectedVehicle?.Id || selectedVehicle?.id;
      const routeGroups = await comms.getAvailableRoutes();
      
      // Check if any route in any group is already assigned to this vehicle
      let filteredRoute = null;
      for (const group of routeGroups) {
        filteredRoute = group.routes.find((r) => {
          return (r.VehicleId === vehicleId);
        });
        if (filteredRoute) break;
      }
      
      if (filteredRoute) {
        setSelectedRoute(filteredRoute);
        try {
          const routeDetails = await comms.getRouteDetails(
            filteredRoute.FixedRouteId || filteredRoute.RouteId || filteredRoute.Id,
            filteredRoute.schedules?.[0]?.Id,
            filteredRoute.runId,
            vehicleId
          );

          // Store route selection
       /*   localStorage.setItem("selectedRouteId", filteredRoute.FixedRouteId);
          localStorage.setItem("selectedRoute", JSON.stringify(filteredRoute));
          localStorage.setItem("routeDetails", JSON.stringify(routeDetails));
*/
          setConfirmDialogOpen(false);
          onRouteSelected( routeDetails);
        } catch (error) {
          console.error("Failed to fetch route details:", error);
          setError("Failed to load route details. Please try again.");
          setConfirmDialogOpen(false);
        }
      } else {
        setRoutes(routeGroups);
      }
    } catch (error) {
      console.error("Failed to fetch routes:", error);
      setError("Failed to load routes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRouteClick = (route) => {
    // Don't allow selection if route already has a vehicle assigned
    if (route.VehicleId) {
      return;
    }
    
    // Don't allow selection if route is time-disabled
    if (isRouteTimeDisabled(route)) {
      return;
    }
    
    setSelectedRoute(route);
    setConfirmDialogOpen(true);
  };

  const handleConfirm = async () => {
    
    if (selectedRoute) {
      try {
        // Get driver ID from user settings
        const user = JSON.parse(localStorage.getItem('userSettings')) || {};
        const driverId = user.Id;
        
        // Assign vehicle to route - now returns complete dispatch data
        const assignResponse = await comms.assignVehicleToRoute(
          selectedRoute.FixedRouteId || selectedRoute.RouteId || selectedRoute.Id,
          selectedVehicle?.Id || selectedVehicle?.id,
          selectedRoute.schedules?.[0]?.Id || null,
          selectedRoute.Name,
          selectedRoute.sourceType,
          driverId
        );

        // Check if we got the complete dispatch data from the assignment
        if (assignResponse.success && assignResponse.dispatchData && assignResponse.dispatchData.routeObject) {
          // Use the route data from the assignment response
          const routeDetails = assignResponse.dispatchData.routeObject;
          
          console.log('Route assignment successful with complete route data:', routeDetails);
          
          setConfirmDialogOpen(false);
          onRouteSelected(routeDetails);
        } else {
          // Fallback to original behavior if assignment didn't return complete data
          console.log('Assignment response missing complete data, fetching route details separately');
          
          const routeDetails = await comms.getRouteDetails(
            selectedRoute.FixedRouteId || selectedRoute.RouteId || selectedRoute.Id,
            selectedRoute.schedules?.[0]?.Id,
            selectedRoute.runId,
            selectedVehicle?.Id || selectedVehicle?.id
          );

          setConfirmDialogOpen(false);
          onRouteSelected(routeDetails);
        }
      } catch (error) {
        console.error("Failed to assign route or fetch route details:", error);
        setError("Failed to assign route or load route details. Please try again.");
        setConfirmDialogOpen(false);
      }
    }
  };

  const handleCancel = () => {
    setSelectedRoute(null);
    setConfirmDialogOpen(false);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // Helper function to check if a route should be greyed out based on time
  const isRouteTimeDisabled = (route) => {
    if (!route.StartDateTime) return false;
    
    const now = new Date();
    const startTime = new Date(route.StartDateTime);
    
    // Calculate time difference in hours
    const timeDiffHours = (startTime - now) / (1000 * 60 * 60);
    
    // Grey out if current time is more than 1 hour before start time or more than 7 hours after start time
    return false;//timeDiffHours < -1 || timeDiffHours > 7;
  };

  // Helper function to check if entire group should be disabled
  const isGroupDisabled = (group) => {
    return !group.isActive;
  };



  // Routes are already grouped by the backend
  const groupedRoutes = routes;

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{ backgroundColor: "white" }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100vh",
        backgroundColor: "white",
        padding: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h6" align="center" sx={{ mb: 1, fontWeight: "bold" }}>
        Select Route
      </Typography>

      {error && (
        <Typography color="error" align="center" sx={{ mb: 1, fontSize: "0.875rem" }}>
          {error}
        </Typography>
      )}

      {groupedRoutes.length === 0 ? (
        <Box textAlign="center" sx={{ py: 2 }}>
          <Typography color="textSecondary" variant="body2">
            No available routes found for your vehicle type.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ flexGrow: 1, overflow: "auto" }}>
          {groupedRoutes.map((group, groupIndex) => {
            const groupDisabled = isGroupDisabled(group);
            
            return (
            <Accordion
              key={group.fixedRouteId || groupIndex}
              expanded={expandedAccordion === `panel${groupIndex}`}
              onChange={groupDisabled ? undefined : handleAccordionChange(`panel${groupIndex}`)}
              disabled={groupDisabled}
              sx={{
                boxShadow: "none",
                border: "1px solid #e0e0e0",
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: 0 },
                mb: 0.5,
                opacity: groupDisabled ? 0.5 : 1,
                backgroundColor: groupDisabled ? "grey.50" : "transparent",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 40,
                  "&.Mui-expanded": { minHeight: 40 },
                  "& .MuiAccordionSummary-content": {
                    margin: "8px 0",
                    "&.Mui-expanded": { margin: "8px 0" },
                  },
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ 
                    fontWeight: "bold", 
                    color: groupDisabled ? "text.disabled" : "primary.main"
                  }}
                >
                  {group.headerName}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1, px: 2 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 0.75,
                  }}
                >
                  {group.routes.map((route) => {
                    const routeTimeDisabled = group.type === 'itinerary' ? isRouteTimeDisabled(route) : false;
                    const routeDisabled = !!route.VehicleId || routeTimeDisabled || groupDisabled;
                    
                    return (
                    <Button
                      key={route.Id}
                      variant="outlined"
                      disabled={routeDisabled}
                      onClick={() => handleRouteClick(route)}
                      sx={{
                        minHeight: 36,
                        px: 1,
                        py: 0.5,
                        fontSize: "0.875rem",
                        border: "1px solid",
                        borderColor: routeDisabled
                          ? "grey.300"
                          : "primary.main",
                        backgroundColor: routeDisabled
                          ? "grey.50"
                          : "transparent",
                        color: routeDisabled
                          ? "text.disabled"
                          : "text.primary",
                        borderRadius: 1,
                        textTransform: "none",
                        opacity: routeTimeDisabled ? 0.4 : 1,
                        "&:hover": {
                          backgroundColor: routeDisabled
                            ? "grey.50"
                            : "primary.light",
                          borderColor: routeDisabled
                            ? "grey.300"
                            : "primary.dark",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "grey.50",
                          borderColor: "grey.300",
                          color: "text.disabled",
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          textAlign: "center",
                          lineHeight: 1.2,
                        }}
                      >
                        {route.Name}
                      </Typography>
                    </Button>
                    );
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
            );
          })}
        </Box>
      )}

      <Box display="flex" justifyContent="center" sx={{ mt: 1, flexShrink: 0 }}>
        <Button variant="outlined" onClick={onBack} sx={{ minWidth: 100, height: 36 }}>
          Back
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCancel}>
        <DialogTitle>Confirm Route Selection</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            {selectedRoute?.Name}
          </Typography>
          {selectedRoute?.StartDateTime && (
            <Typography variant="body2" color="textSecondary">
              Schedule: {new Date(selectedRoute.StartDateTime).toLocaleTimeString()} - {selectedRoute.EndDateTime ? new Date(selectedRoute.EndDateTime).toLocaleTimeString() : 'End of day'}
            </Typography>
          )}
          {selectedRoute?.Description && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {selectedRoute.Description}
            </Typography>
          )}
          <Typography variant="body1" sx={{ mt: 2 }}>
            Are you sure you want to select this route?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RouteSelection;
